<template>
  <div class="flex flex-col h-full">
    <!-- Header with Filters (Fixed) -->
    <div class="sticky top-0 z-[10] bg-white">
      <div class="p-4 border-b border-gray-200">
        <div class="flex justify-between items-center">
          <h2 class="text-sm font-medium text-gray-700">조회 결과</h2>

          <div class="flex items-center gap-4">
            <!-- 라벨 필터 -->
            <div class="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300">
              <!-- 라벨 목록 -->
              <button v-for="label in labels" :key="label.id" @click="toggleLabelFilter(label.id)"
                class="w-4 h-4 rounded-full transition-all hover:scale-110" :style="{
                  backgroundColor: activeLabelFilters.includes(label.id) ? label.colorCode : 'transparent',
                  border: `2.5px solid ${label.colorCode}`,
                }" :title="label.name"></button>

              <!-- 구분선 -->
              <div class="w-px h-4 bg-gray-300 mx-0.5"></div>

              <!-- 라벨 관리 버튼 -->
              <button @click="showLabelManager = true"
                class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                title="라벨 관리">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>

            <!-- 저쿠폰 필터 버튼 -->
            <div class="flex">
              <button @click="toggleFilter" class="px-3 py-1.5 text-sm rounded-l-md border border-r-0 flex items-center"
                :class="isLowCouponFilterActive
                  ? 'bg-blue-50 border-blue-500 text-blue-700'
                  : 'border-gray-300 text-gray-700'
                  ">
                저쿠폰
              </button>
              <div @click="startEdit"
                class="threshold-edit px-3 py-1.5 text-sm rounded-r-md border flex items-center gap-2 min-w-[80px] justify-between group cursor-pointer"
                :class="[
                  isLowCouponFilterActive
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-300 text-gray-700',
                  showError ? 'border-red-500' : '',
                ]">
                <div class="flex items-center gap-1">
                  <template v-if="!isEditing">
                    {{ couponRateThreshold }}%
                  </template>
                  <input v-else v-model="couponRateInput" type="number" step="0.001"
                    class="w-14 py-0 text-sm bg-transparent border-0 focus:ring-0 focus:outline-none"
                    :class="{ 'text-red-500': showError }" @keydown="handleKeyDown" @blur="finishEdit" ref="inputRef" />
                </div>
                <svg class="w-3 h-3 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none"
                  stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>

            <!-- ESG 필터 버튼 -->
            <button @click="toggleEsgFilter" class="px-3 py-1.5 text-sm rounded-md border" :class="isEsgFilterActive
              ? 'bg-blue-50 border-blue-500 text-blue-700'
              : 'border-gray-300 text-gray-700'
              ">
              ESG
            </button>

            <!-- 기존 검색창 -->
            <div class="relative w-64">
              <input v-model="inputKeyword" type="text" placeholder="검색어를 입력하세요"
                class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                @keyup.enter="applySearch" />
              <!-- 초기화 버튼 -->
              <button v-if="inputKeyword" @click="clearSearch"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <button @click="$emit('refresh')" class="text-gray-600 hover:text-gray-900" :disabled="isLoading">
              <div class="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="{ 'animate-spin': isLoading }"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span class="text-sm">새로고침</span>
              </div>
            </button>
            <div class="text-sm text-gray-600">
              <slot name="analysis-time"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 저쿠폰 설정 모달 -->
    <div v-if="showCouponRateInput" class="fixed inset-0 flex items-center justify-center z-[999]"
      @click.self="showCouponRateInput = false">
      <div class="bg-white rounded-lg shadow-lg p-4 w-64">
        <div class="text-sm font-medium mb-3">저쿠폰 기준값 설정</div>
        <div class="flex items-center gap-2">
          <input v-model="couponRateInput" type="number" step="0.001"
            class="w-full px-2 py-1 text-sm border border-gray-300 rounded" @keyup.enter="applyCouponRate" />
          <span class="text-sm text-gray-500">%</span>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button @click="applyCouponRate" class="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">
            확인
          </button>
          <button @click="showCouponRateInput = false"
            class="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
            취소
          </button>
        </div>
      </div>
    </div>

    <!-- Scrollable Content Area -->
    <div class="flex-1 overflow-auto">
      <SoldOutAsksComponent :asks="soldOutAsks" />
      <ForSaleAsksComponent :asks="forSaleAsks" />
    </div>

    <!-- 라벨 관리 팝업 -->
    <LabelManager v-model="showLabelManager" />
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useStore } from 'vuex';
import SoldOutAsksComponent from './asks/SoldOutAsksComponent.vue';
import ForSaleAsksComponent from './asks/ForSaleAsksComponent.vue';
import LabelManager from './common/LabelManager.vue';
import { useQuoteLabel } from '@/composables/useQuoteLabel';

export default {
  name: 'CreditAskResultComponent',
  components: {
    SoldOutAsksComponent,
    ForSaleAsksComponent,
    LabelManager,
  },
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    maturityRange: {
      type: Array,
      required: true,
    },
    searchKeyword: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:searchKeyword', 'refresh'],
  setup(props, { emit }) {
    const store = useStore();
    const { labels, fetchLabels, getQuoteLabels } = useQuoteLabel();

    // 입력 중인 검색어
    const inputKeyword = ref('');
    // 실제 필터링에 사용될 검색어
    const activeKeyword = ref('');

    // 라벨 관련
    const showLabelManager = ref(false);
    const activeLabelFilters = ref([]);

    // ESG 필터 관련
    const isEsgFilterActive = ref(false);
    const toggleEsgFilter = () => {
      isEsgFilterActive.value = !isEsgFilterActive.value;
    };

    // 라벨 필터 토글
    const toggleLabelFilter = (labelId) => {
      const index = activeLabelFilters.value.indexOf(labelId);
      if (index > -1) {
        activeLabelFilters.value.splice(index, 1);
      } else {
        activeLabelFilters.value.push(labelId);
      }
    };

    // store에서 저쿠폰 필터 상태 가져오기
    const isLowCouponFilterActive = computed(
      () => store.getters['filter/isLowCouponActive']
    );
    const couponRateThreshold = computed(
      () => store.getters['filter/lowCouponThreshold']
    );

    const couponRateInput = ref(couponRateThreshold.value.toString());
    const showCouponRateInput = ref(false);
    const isEditing = ref(false);
    const inputRef = ref(null);
    const showError = ref(false);

    // 저쿠폰 필터 토글
    const toggleFilter = e => {
      e.stopPropagation();
      store.commit('filter/setLowCouponActive', !isLowCouponFilterActive.value);
    };

    // 저쿠폰 임계값 수정
    const finishEdit = () => {
      const value = parseFloat(couponRateInput.value);
      if (!isNaN(value) && value >= 0 && value <= 3) {
        store.commit('filter/setLowCouponThreshold', value);
        showError.value = false;
      } else {
        showError.value = true;
        couponRateInput.value = couponRateThreshold.value.toString();
        setTimeout(() => {
          showError.value = false;
        }, 1000);
      }
      isEditing.value = false;
    };

    const handleKeyDown = e => {
      if (e.key === 'Enter') {
        e.preventDefault(); // 이벤트 전파 방지
        finishEdit();
      }
    };

    // 검색어 적용
    const applySearch = () => {
      activeKeyword.value = inputKeyword.value;
      emit('update:searchKeyword', inputKeyword.value);
    };

    // 검색어 초기화
    const clearSearch = () => {
      inputKeyword.value = '';
      activeKeyword.value = '';
      emit('update:searchKeyword', '');
    };

    // 외부에서 searchKeyword가 변경될 경우 동기화
    watch(
      () => props.searchKeyword,
      newValue => {
        inputKeyword.value = newValue;
        activeKeyword.value = newValue;
      }
    );

    // 모든 필터를 적용한 결과
    const filteredAsks = computed(() => {
      let filtered = props.modelValue;

      // 만기 필터 적용
      if (props.maturityRange) {
        const [minMonths, maxMonths] = props.maturityRange;
        filtered = filtered.filter(ask => {
          const maturityDate = new Date(ask.maturityDate);
          const today = new Date();
          const monthsDiff =
            (maturityDate.getFullYear() - today.getFullYear()) * 12 +
            (maturityDate.getMonth() - today.getMonth());
          return (
            monthsDiff >= minMonths &&
            (maxMonths === 61 || monthsDiff <= maxMonths)
          );
        });
      }

      // 라벨 필터 적용
      if (activeLabelFilters.value.length > 0) {
        filtered = filtered.filter(ask => {
          const quoteLabels = getQuoteLabels(ask.id);
          return quoteLabels.some(label => activeLabelFilters.value.includes(label.id));
        });
      }

      // ESG 필터 적용
      if (isEsgFilterActive.value) {
        filtered = filtered.filter(ask => ask.isEsg === true);
      }

      // 저쿠폰 필터 적용
      if (isLowCouponFilterActive.value) {
        filtered = filtered.filter(
          ask => ask.couponRate <= couponRateThreshold.value
        );
      }

      // 검색어 필터 적용
      if (activeKeyword.value) {
        const keyword = activeKeyword.value.toLowerCase();
        filtered = filtered.filter(
          ask =>
            ask.bondName?.toLowerCase().includes(keyword) ||
            ask.content?.toLowerCase().includes(keyword)
        );
      }

      return filtered;
    });

    const soldOutAsks = computed(() => {
      return filteredAsks.value.filter(ask => ask.hasTraded === true);
    });

    const forSaleAsks = computed(() => {
      return filteredAsks.value.filter(ask => ask.hasTraded === false || ask.hasTraded === undefined);
    });

    const startEdit = e => {
      e.stopPropagation();
      if (e.target.closest('.threshold-edit')) {
        isEditing.value = true;
        nextTick(() => {
          inputRef.value?.select();
        });
      }
    };

    const applyCouponRate = () => {
      // Implementation of applyCouponRate
    };

    // 컴포넌트 마운트 시 라벨 목록 조회
    onMounted(async () => {
      await fetchLabels();
      console.log('라벨 목록 조회 완료:', labels.value);
    });

    return {
      inputKeyword,
      applySearch,
      clearSearch,
      soldOutAsks,
      forSaleAsks,
      isEsgFilterActive,
      toggleEsgFilter,
      isLowCouponFilterActive,
      toggleFilter,
      couponRateThreshold,
      couponRateInput,
      showCouponRateInput,
      isEditing,
      inputRef,
      startEdit,
      finishEdit,
      handleKeyDown,
      showError,
      applyCouponRate,
      labels,
      showLabelManager,
      activeLabelFilters,
      toggleLabelFilter,
    };
  },
};
</script>

<style scoped>
/* 숫자 입력 화살표 제거 */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
